/**
 * The till's dispatch wire shapes (`/api/restaurant/dispatch/**`, driver
 * backend B3 · P13). Mirrors `apps/backend/src/modules/restaurant/types/
 * restaurant-dispatch.types.ts`. Kept out of `./types.ts` on purpose (plan
 * D-34): every dispatch / driver / tracking type lives here.
 */
import type { OffPremiseGridEntry } from "./types";

/** The contract's assignment statuses (no EXPIRED: an expired offer is DECLINED). */
export type DispatchStatus =
  | "OFFERED"
  | "ACCEPTED"
  | "DECLINED"
  | "PICKED_UP"
  | "ARRIVED"
  | "DELIVERED"
  | "FAILED"
  | "RETURNED"
  | "CANCELLED";

/** Why an assignment ended (null while it is live). */
export type DispatchEndReason =
  | "DECLINED"
  | "EXPIRED"
  | "WITHDRAWN"
  | "REASSIGNED"
  | "RESOLVED_CANCELLED";

/** One assignment as the till sees it. Times are ISO strings. */
export type DispatchAssignmentView = {
  id: string;
  invoiceId: string;
  driverSellerId: string;
  driverName: string;
  status: DispatchStatus;
  endReason: DispatchEndReason | null;
  offeredAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  pickedUpAt: string | null;
  arrivedAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  returnedAt: string | null;
  endedAt: string | null;
  problemOpen: boolean;
  /** The unresolved report while `problemOpen` (B3 · P14), else null. */
  problem: DispatchOpenProblem | null;
  cashCollected: number | null;
};

/** The driver's problem reasons (contract #9). */
export type DispatchProblemReason =
  | "NO_ANSWER"
  | "WRONG_ADDRESS"
  | "REFUSED"
  | "MISSING_OR_DAMAGED"
  | "OTHER";

/** An unresolved problem report on the board. */
export type DispatchOpenProblem = {
  id: string;
  /** One of `DispatchProblemReason`; read as a string (a newer reason must not crash the till). */
  reason: string;
  note: string | null;
  reportedAt: string;
};

/** A driver on the board (T1). */
export type DispatchBoardDriver = {
  sellerId: string;
  name: string;
  phone: string;
  onShift: boolean;
  shiftStartedAt: string | null;
  /** Live assignments (offered … returning) the driver holds now. */
  activeCount: number;
  /** Cash of delivered bags not yet handed in. */
  cashInHand: number;
  /** The amount of the driver's PENDING handover, or null. */
  pendingHandoverAmount: number | null;
  /** The id of that PENDING handover (what confirm / reject take), or null. */
  pendingHandoverId: string | null;
  /** B4 (live positions); null until then. */
  lastPosition: { lat: number; lng: number; at: string } | null;
};

/** T1 `GET restaurant/dispatch/board`. */
export type DispatchBoardResponse = {
  serverTime: string;
  /** Eligible drivers, on shift first, then by name. */
  drivers: DispatchBoardDriver[];
  /** Live assignments + delivered bags whose bill is still CLOSED. */
  assignments: DispatchAssignmentView[];
  /** Offers declined, expired or withdrawn in the last few minutes, newest first ("X did not answer"). */
  endedOffers: DispatchAssignmentView[];
  /**
   * Deliveries ended after pickup (RETURNED, or CANCELLED by the till) whose
   * bill is still CLOSED, newest per bill (B3 · P14 rework). Optional so a
   * board from an older build still reads.
   */
  endedDeliveries?: DispatchAssignmentView[];
};

/** Till "end a driver's shift" answer (B3 · P14 rework). */
export type DispatchDriverShiftEndResponse = {
  applied: boolean;
  serverTime: string;
  driverSellerId: string;
  shift: { id: string; startedAt: string; endedAt: string | null } | null;
};

/** T2 assign · T3 reassign · T4 withdraw answer. */
export type DispatchAssignmentCommandResponse = {
  applied: boolean;
  serverTime: string;
  assignment: DispatchAssignmentView;
};

/** T8 `GET/PUT restaurant/dispatch/settings`. */
export type DispatchSettingsView = {
  deliveryAcceptTimeoutMinutes: number;
};

/** The accept-timeout bounds (the DB CHECK, `dispatch.constants.ts`). */
export const DISPATCH_TIMEOUT_MIN_MINUTES = 1;
export const DISPATCH_TIMEOUT_MAX_MINUTES = 15;

/* ── B3 · P14: problems, the stranded-bag end, cash handovers ─────────── */

/** T5 resolutions (plan D-15). */
export type DispatchProblemResolution = "RETRY" | "RETURN_TO_STORE" | "CANCELLED";

/** T5 answer. */
export type DispatchProblemResolveResponse = DispatchAssignmentCommandResponse & {
  problem: {
    id: string;
    reason: string;
    note: string | null;
    reportedAt: string;
    resolution: string | null;
    resolutionNote: string | null;
    resolvedAt: string | null;
  } | null;
};

/** T16 outcomes: the bag came back, or the delivery is called off. */
/** DELIVERED (2026-10-27): the cashier checked with the customer; never from FAILED. */
export type DispatchEndOutcome = "RETURNED" | "CANCELLED" | "DELIVERED";

export type DispatchHandoverStatus = "PENDING" | "CONFIRMED" | "REJECTED";

/** The till's view of a driver's cash handover (T6/T7). */
export type DispatchHandoverView = {
  id: string;
  driverSellerId: string;
  driverName: string;
  /** What the driver handed in — still counts bags the sitting settled since. */
  amount: number;
  status: DispatchHandoverStatus;
  submittedAt: string;
  confirmedAt: string | null;
  confirmedByName: string | null;
  rejectedAt: string | null;
  rejectNote: string | null;
  items: { assignmentId: string; invoiceId: string; cashCollected: number }[];
};

/** What the confirm would do with one bag now. */
export type DispatchHandoverBagOutcome = "SETTLE" | "SKIP" | "CHANGED";

/** `GET restaurant/dispatch/handovers/:id`: the handover and the confirm's decision per bag. Sums are the server's. */
export type DispatchHandoverPreview = {
  serverTime: string;
  handover: DispatchHandoverView;
  bags: {
    assignmentId: string;
    invoiceId: string;
    orderNumber: number | null;
    cashCollected: number;
    outcome: DispatchHandoverBagOutcome;
  }[];
  settleAmount: number;
  settleCount: number;
  skipAmount: number;
  skipCount: number;
  changedCount: number;
};

/** T6 confirm · T7 reject answer. */
export type DispatchHandoverCommandResponse = {
  applied: boolean;
  serverTime: string;
  handover: DispatchHandoverView;
  /** The bills this call settled (T6). */
  settledInvoiceIds: string[];
  /** The bills it found already SETTLED at the sitting and left alone (T6). */
  skippedInvoiceIds: string[];
  /** Σ cash of the settled bags — what the till shows as settled (not `handover.amount`). */
  settledAmount: number;
  skippedAmount: number;
};

/* ── B4 · P16: the tracking link (T9/T10) and the customer's public read ── */

/** The tracking link as the till sees it (T9 mint/read, T10 shared). */
export type DispatchTrackingLinkView = {
  invoiceId: string;
  /** The full https link to share, e.g. `https://restaurant.<base>/ar/track/<token>`. */
  url: string;
  /** The same without the scheme. */
  displayUrl: string;
  status: "NOT_SENT" | "SENT";
  sharedAt: string | null;
  createdAt: string;
  /** When the public read stops answering; always present. */
  expiresAt: string;
};

/** T9 `POST restaurant/dispatch/invoices/:invoiceId/tracking-link`. */
export type DispatchTrackingLinkResponse = {
  serverTime: string;
  link: DispatchTrackingLinkView;
};

/** T10 `POST restaurant/dispatch/invoices/:invoiceId/tracking-link/shared`. */
export type DispatchTrackingSharedResponse = {
  applied: boolean;
  serverTime: string;
  link: DispatchTrackingLinkView;
};

/** Where the customer's order is (public read, plan D-29). */
export type PublicDispatchTrackingStage =
  | "PREPARING"
  | "ON_THE_WAY"
  | "ARRIVED"
  | "DELIVERED"
  | "NOT_DELIVERED";

/**
 * `GET /api/public/restaurant/dispatch-tracking/:token` — mirrors the
 * backend's `PublicDispatchTrackingView`. Carries NO customer name, phone or
 * address text; the driver's first name, phone and position only while the bag
 * is on its way (and the position only while fresh, plan D-25).
 */
export type PublicDispatchTrackingView = {
  serverTime: string;
  stage: PublicDispatchTrackingStage;
  orderNumber: number;
  restaurant: {
    name: string;
    logoUrl: string | null;
    lat: number | null;
    lng: number | null;
  };
  timeline: {
    acceptedAt: string | null;
    pickedUpAt: string | null;
    arrivedAt: string | null;
    deliveredAt: string | null;
  };
  driver: { firstName: string; phone: string } | null;
  driverPosition: { lat: number; lng: number; at: string } | null;
  destination: { lat: number; lng: number } | null;
  route: {
    distanceMeters: number;
    durationSeconds: number;
    polyline: string | null;
  } | null;
  eta: { minMinutes: number; maxMinutes: number } | null;
  /** Always a string since G15 (the link always ends); typed as given. */
  expiresAt: string;
};

/* ── Driver roster and documents (`/api/restaurant/drivers/**`, driver backend
   B5 · T11–T15). Mirrors `restaurant-driver-document.types.ts`. A document is
   never a key or a URL here: the only URL is T13's 60-second answer, which the
   till opens once and never keeps. ─────────────────────────────────────── */

/** The driver app contract's eight kinds. */
export type DriverDocumentKind =
  | "PROFILE_PHOTO"
  | "ID_FRONT"
  | "ID_BACK"
  | "SELFIE_WITH_ID"
  | "CRIMINAL_RECORD"
  | "DRIVING_LICENCE"
  | "VEHICLE_LICENCE"
  | "VEHICLE_PHOTO";

/** The review order the till lists them in (the app's 12a → 12c order). */
export const DRIVER_DOCUMENT_KINDS: readonly DriverDocumentKind[] = [
  "PROFILE_PHOTO",
  "ID_FRONT",
  "ID_BACK",
  "SELFIE_WITH_ID",
  "CRIMINAL_RECORD",
  "DRIVING_LICENCE",
  "VEHICLE_LICENCE",
  "VEHICLE_PHOTO",
];

export type DriverDocumentStatus = "SUBMITTED" | "ACCEPTED" | "RESUBMIT";

export type DriverDocumentContentType = "image/jpeg" | "image/png" | "application/pdf";

export type DriverVehicleType = "MOTORBIKE" | "CAR" | "BICYCLE" | "FOOT";

export type RosterVehicleView = {
  vehicleType: DriverVehicleType | null;
  plateLetters: string | null;
  plateNumbers: string | null;
};

export type RosterDocumentState = { kind: DriverDocumentKind; status: DriverDocumentStatus };

/** T11 row. `documents` is null without `RESTAURANT_DRIVER_DOCUMENTS_VIEW`. */
export type RosterDriverSummary = {
  sellerId: string;
  name: string;
  phone: string;
  onShift: boolean;
  vehicle: RosterVehicleView;
  documents: RosterDocumentState[] | null;
};

export type DriversRosterResponse = { drivers: RosterDriverSummary[] };

export type RosterDocumentView = {
  id: string;
  kind: DriverDocumentKind;
  contentType: DriverDocumentContentType;
  status: DriverDocumentStatus;
  note: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
  reviewedBySellerId: string | null;
};

/** T12. `documents` is null without `RESTAURANT_DRIVER_DOCUMENTS_VIEW`. */
export type RosterDriverDetails = {
  sellerId: string;
  name: string;
  phone: string;
  onShift: boolean;
  nationalIdLast4: string | null;
  vehicle: RosterVehicleView;
  consentGivenAt: string | null;
  documents: RosterDocumentView[] | null;
};

export type DriverDetailsResponse = { driver: RosterDriverDetails };

/** T13 — opened once, at once; never stored, never logged. */
export type DriverDocumentViewUrlResponse = { url: string; expiresAt: string };

export type DriverDocumentDecision = "ACCEPTED" | "RESUBMIT";

/** T14 note bound (the backend's `DRIVER_DOCUMENT_NOTE_MAX_LENGTH`). */
export const DRIVER_DOCUMENT_NOTE_MAX = 500;

export type DriverDocumentReviewResponse = { applied: boolean; document: RosterDocumentView };

export type DriverDocumentAccessLogEntry = {
  id: string;
  documentId: string;
  documentKind: DriverDocumentKind;
  viewerSellerId: string;
  viewerName: string | null;
  viewedAt: string;
};

/** T15 — who opened which document, newest first. */
export type DriverDocumentViewsResponse = { views: DriverDocumentAccessLogEntry[] };

/**
 * B6 D-37 — an off-premise floor bill as the Delivery & takeaway tab reads it:
 * the grid entry plus `readySince` (ISO), the latest `ready_at` among the
 * chargeable plates once every one of them is READY or SERVED, else null.
 * Optional on the wire, so an API older than P21 still reads (no "Ready Nm").
 */
export type DeliveryTabGridEntry = OffPremiseGridEntry & {
  readySince?: string | null;
  /**
   * B6 · P28a (C10 carry) — the customer's map pin on the storefront request
   * this bill was accepted from (#238), numbers + `source` (AREA_FALLBACK =
   * approximate); null / absent otherwise or on an older API. Shown through
   * `deliveryMapUrl` / `deliveryPinIsApproximate`, never a raw URL.
   */
  deliveryPin?: { lat: number; lng: number; source: string } | null;
};

/* ── B6 · P25: the cashier's driver settlements (plan D-41 … D-45) ───────────
 * Mirrors `apps/backend/src/modules/restaurant/types/restaurant-driver-settlement.types.ts`
 * and `constants/driver-settlement.constants.ts` (committed P23). The server
 * computes every figure; the till only displays and echoes them. */

/** Who a settlement card is about (D-41). */
export type DriverSettlementSubjectKind = "DRIVER" | "RIDER" | "PLATFORM";

/** Where a bag stands for the settlement (D-42's status chip). */
export type SettlementBagState =
  | "ON_THE_WAY"
  | "WAITING_PICKUP"
  | "DELIVERED"
  | "RETURNED"
  | "VOIDED"
  | "OUT_WITH_RIDER";

/** What the cashier does with one bag (the request vocabulary). */
export type DriverSettlementOutcome =
  | "SETTLE_CASH"
  | "SETTLE_INSTAPAY"
  | "SETTLE_AGGREGATOR"
  | "ALREADY_PAID"
  | "RETURNED"
  | "VOIDED"
  | "LEAVE_OWED";

/** The stored item outcome. */
export type DriverSettlementItemOutcome =
  | "SETTLED_CASH"
  | "SETTLED_INSTAPAY"
  | "SETTLED_AGGREGATOR"
  | "ALREADY_PAID"
  | "RETURNED"
  | "VOIDED"
  | "LEFT_OWED";

/** How the driver was paid at the door (P22). */
export type DispatchDoorMethod = "CASH" | "INSTAPAY";

export type DriverSettlementSubjectRef = {
  kind: DriverSettlementSubjectKind;
  driverSellerId: string | null;
  /** `riderNameKey(handed_to)`; `""` = «من غير اسم». Null for a driver. */
  riderKey: string | null;
  aggregatorId: string | null;
};

/** One bag on a card / in the settle panel (D-42). */
export type DriverSettlementBagView = {
  invoiceId: string;
  assignmentId: string | null;
  orderNumber: number;
  customerName: string | null;
  state: SettlementBagState;
  dispatchStatus: DispatchStatus | null;
  aggregatorId: string | null;
  doorMethod: DispatchDoorMethod | null;
  settlementMethod: string | null;
  billStatus: string;
  /** Send back as `expectedVersion`. */
  version: number;
  total: number;
  tendered: number;
  /** What the bill owes NOW (the amount a settle takes). */
  due: number;
  /** What the driver was told to collect at pickup. */
  collectedAtPickup: number | null;
  cashCollected: number | null;
  instapayAmount: number | null;
  hasProof: boolean;
  changedAfterPickup: boolean;
  /** Part of the run (must be listed in the command). */
  onRun: boolean;
  /** First = the default; empty for a bag that is not on the run. */
  allowedOutcomes: DriverSettlementOutcome[];
  defaultOutcome: DriverSettlementOutcome | null;
  inPendingHandover: boolean;
  leftOwedBefore: boolean;
};

export type DriverSettlementPreviewSums = {
  cashExpected: number;
  instapayTotal: number;
  aggregatorTotal: number;
  alreadyPaidTotal: number;
  returnedTotal: number;
  leftOwedTotal: number;
};

/** A settlement card (D-41). */
export type DriverSettlementCardView = {
  subject: DriverSettlementSubjectRef;
  /** `""` for «من غير اسم» (the till labels it). */
  name: string;
  /** Driver: phone last 4; rider / platform: null. */
  code: string | null;
  aggregatorName: string | null;
  bags: DriverSettlementBagView[];
  counts: {
    onRun: number;
    done: number;
    onTheWay: number;
    waitingPickup: number;
    /** Bags the driver reported coming back (FAILED): the only ones that block Settle. */
    comingBack: number;
    returned: number;
    voided: number;
    leftOwedBefore: number;
  };
  sums: DriverSettlementPreviewSums;
  pendingHandover: { id: string; amount: number } | null;
  needsSettlement: boolean;
  hasReturns: boolean;
  lastSettledToday: { settlementId: string; confirmedAt: string } | null;
};

/** T20 `GET restaurant/dispatch/settlements/open`. */
export type DriverSettlementsOpenResponse = {
  serverTime: string;
  businessDate: string;
  cards: DriverSettlementCardView[];
};

export type DriverSettlementItemView = {
  id: string;
  invoiceId: string;
  assignmentId: string | null;
  orderNumber: number | null;
  outcome: DriverSettlementItemOutcome;
  amount: number;
  settleCommandId: string | null;
};

/** A stored settlement (T21, the command's answer). */
export type DriverSettlementView = {
  id: string;
  subject: DriverSettlementSubjectRef;
  name: string;
  riderName: string | null;
  shiftId: string | null;
  businessDate: string;
  bagCount: number;
  expectedCash: number;
  countedCash: number;
  /** counted − expected: < 0 short, > 0 over. */
  difference: number;
  instapayTotal: number;
  aggregatorTotal: number;
  alreadyPaidTotal: number;
  payoutAmount: number;
  payoutMovementId: string | null;
  differenceMovementId: string | null;
  returnedAcknowledged: boolean;
  confirmedBySellerId: string;
  confirmedByName: string | null;
  confirmedAt: string;
  commandId: string;
  items: DriverSettlementItemView[];
  handover: { id: string; status: string } | null;
};

/** T22 `POST restaurant/dispatch/settlements` answer. */
export type DriverSettlementCommandResponse = {
  applied: boolean;
  serverTime: string;
  settlement: DriverSettlementView;
};

/** The T22 body without the envelope (`id`, `clientTimestamp`). */
export type DriverSettlementRequestBody = {
  subject: {
    kind: DriverSettlementSubjectKind;
    driverSellerId?: string;
    riderKey?: string;
    aggregatorId?: string;
  };
  bags: Array<{
    invoiceId: string;
    assignmentId?: string;
    expectedVersion: number;
    outcome: DriverSettlementOutcome;
  }>;
  returnedAcknowledged: boolean;
  countedCash: number;
  expectedCash: number;
  payout?: { amount: number; reason: string };
};

/** T19 `POST restaurant/dispatch/assignments/:id/payment-proof/view` — a 60 s link, never stored. */
export type DispatchPaymentProofViewResponse = {
  url: string;
  contentType: "image/jpeg" | "image/png";
  expiresAt: string;
};
